/* eslint-disable no-case-declarations */
import {
  formatDate,
  getDayStart,
  getDayEnd,
  getEpochDayStart,
  getEpochDayEnd,
  convertTimeSpanToSQLDays
} from './dateUtils';
import { getGenericFieldType } from './genericUtils';
import _, { template } from "lodash";
import FilterFieldsOrdering from '../../../defaults/FilterFieldsOrdering.json'
import FieldNames from '../../../defaults/FieldNames.json';
import CustomFilterDefinitions from '../defaults/CustomFilterDefinitions.json';
import { getDatasetFilter } from '../../../utils/ExpressionHelper';

export function buildExpression(options, isForSaving) {
  if (!options || (options && !options.field)) return null;

  const fieldName = (options.field.useExpressionField ? options.field.expressionField : options.field.name);
  const fieldType = getGenericFieldType(options.field.type);
  const inputValue = options.value || '';
  let operator = _.cloneDeep(options.operator);
  if(isForSaving){
    if(operator?.operator){
      const recreatedOperator = operator.operator?.replaceAll('<', '&lt;')?.replaceAll('>', '&gt;');
      operator.operator = recreatedOperator;
    }
  } else {
    if(operator?.operator){
      const recreatedOperator = operator.operator?.replaceAll('&lt;', '<')?.replaceAll('&gt;', '>');
      operator.operator = recreatedOperator;
    }
  }

  let value;
  if (operator && !operator.omitValue) {
    value = `${operator.prefix || ''}${inputValue?.code? inputValue.code : inputValue}${operator.suffix ||
      ''}`;
  }
  
  let expression;
  let checkedValue = (value && value.includes("'")) ? value.replaceAll("'", "''") : value;
  if(operator){
    switch (fieldType) {
      case 'string':
        const splitValue = checkedValue?.split(" - ");
        if(fieldName === CustomFilterDefinitions?.County?.name && splitValue?.length >= 2){
          let modifiedVal = [];
          splitValue.forEach(val => {
            if(operator?.prefix || operator?.suffix){
              let replacedVal = val;
              if(operator.prefix){
                replacedVal = replacedVal?.replaceAll(`${operator.prefix}`, "");
              }
              if(operator.suffix){
                replacedVal = replacedVal?.replaceAll(`${operator.suffix}`, "");
              }
              let prefix = operator?.prefix ? operator.prefix : '';
              let suffix = operator?.suffix ? operator.suffix : '';
              replacedVal = `${prefix}${replacedVal}${suffix}`;
              modifiedVal.push(replacedVal)
            } else {
              modifiedVal.push(val)
            }
          })
          expression = `(${fieldName} ${operator.operator}`;
          expression += operator.omitValue ? '' : ` '${modifiedVal[0]}') AND `;
          expression += `(${CustomFilterDefinitions.State.name} ${operator.operator}`;
          expression += operator.omitValue ? '' : ` '${modifiedVal[1]}')`

        } else {
          if(isForSaving)
          {
            const cleanedFieldName = (operator.alias === 'nonop' ? fieldName?.replaceAll('<', '&lt;')?.replaceAll('>', '&gt;') : fieldName) ?? '';
            expression = `${cleanedFieldName}` + (operator.operator ? ` ${operator.operator}` : '');
          }
          else
          {
            const cleanedFieldName = (operator.alias === 'nonop' ? fieldName?.replaceAll('&lt;', '<')?.replaceAll('&gt;', '>') : fieldName) ?? '';
            expression = `${cleanedFieldName}` + (operator.operator ? ` ${operator.operator}` : '');
          }
          expression += operator.omitValue ? '' : ` '${checkedValue}'`;
        }
        break;
      case 'number':
        expression = `${fieldName} ${operator.operator}`;
        expression += operator.omitValue ? '' : ` ${checkedValue}`;
        break;
      case 'date':
        const d =
          inputValue?.code && inputValue?.code !== ''
            ? new Date(inputValue.code)
            : (inputValue && inputValue !== '' ? new Date(inputValue) : new Date());

        const date = new Date(
          d.getUTCFullYear(),
          d.getUTCMonth(),
          d.getUTCDate(),
        );

        if (operator.fullDay && operator.alias === "is after") {
          expression = `${fieldName} ${operator.operator}`;
          expression += ` date '${getDayEnd(date)}'`;
        }
        else if (operator.fullDay && operator.alias === "is before") {
          expression = `${fieldName} ${operator.operator}`;
          expression += ` date '${getDayStart(date)}'`;
        }
        else if (operator.fullDay) {
          expression = `${fieldName} ${operator.operator}`;
          expression += ` date '${getDayStart(
            date
          )}' AND date '${getDayEnd(date)}'`;
        } else if (operator.relativeDate) {
          expression = `${fieldName} ${operator.operator
            } CURRENT_DATESTAMP - ${convertTimeSpanToSQLDays(
              inputValue?.timeSizeValue,
              inputValue?.timeSpanValue
            )} AND CURRENT_DATESTAMP`;
        }
        else {
          expression = `${fieldName} ${operator.operator}`;
          expression += operator.omitValue
            ? ''
            : ` date '${formatDate(date, 'date')}'`;
        }
        break;
      case 'epoch':
        const epochD =
          inputValue?.code && inputValue?.code !== ''
            ? new Date(inputValue.code)
            : (inputValue && inputValue !== '' ? new Date(inputValue) : new Date());

        const epochDate = new Date(
          epochD.getUTCFullYear(),
          epochD.getUTCMonth(),
          epochD.getUTCDate(),
          epochD.getUTCHours(),
          epochD.getUTCMinutes(),
          epochD.getUTCSeconds()
        );

        expression = `${fieldName} ${operator.operator}`;

        if (operator.fullDay) {
          expression += ` ${getEpochDayStart(epochDate)} AND ${getEpochDayEnd(
            epochDate
          )}`;
        } else if (operator.alias === 'is before') {
          expression += ` ${getEpochDayStart(epochDate)}`;
        } else if (operator.alias === 'is on or before') {
          expression += ` ${getEpochDayEnd(epochDate)}`;
        } else if (operator.alias === 'is on or after') {
          expression += ` ${getEpochDayStart(epochDate)}`;
        } else {
          expression += operator.omitValue
            ? ''
            : ` date '${formatDate(epochDate, 'epoch')}'`;
        }

        break;
      default:
    }
  }
  return expression;
}

export function buildSet(options, isForSaving) {
  // options is an array of expressions and a boolean 'mustMatchAll'
  const { mustMatchAll, expressions } = options;
  const setOperator = mustMatchAll ? 'AND' : 'OR';
  const expKeys = expressions ? Object.keys(expressions) : [];
  let expressionsArr = [];
  if(expKeys && expKeys.length){
    expressionsArr = expKeys.map(key => {
      if(key && expressions && expressions[key] && expressions[key].field && expressions[key].field.name)
      {
        return {order: FilterFieldsOrdering[expressions[key].field.name]?.order, obj: expressions[key]}
      } else {
        return null;
      }
    })?.filter(x => x != null);
  }

  const orderedExpressionsArray = expressionsArr?.sort((a,b) => a?.order - b?.order)
  let set;
  if(orderedExpressionsArray && orderedExpressionsArray.length){
    set = orderedExpressionsArray.map(expressionObj => {
      if(expressionObj){
        let expressionVal = expressionObj.obj
        let expression = "";
        expression = buildExpression(expressionVal, isForSaving);
        if (!expression) return null;
        return `(${expression})`;
      } else {
        return null;
      }
    })?.filter(expression => expression);
  }

  if (set?.length > 1) {
    return "(" + set.join(` ${setOperator} `) + ")";
  }
  else
  {
    return set?.[0] ?? '';
  }
}

export function buildFilter(options, isForSaving, userInfo) {
  let optionsInner = options;
  if(optionsInner){
    if(optionsInner.options){
      optionsInner = optionsInner.options;
    }
    // options is an array of sets and a boolean 'mustMatchAll'
    const mustMatchAll = optionsInner[FieldNames.customFilter.mustMatchAll];
    const sets = optionsInner[FieldNames.customFilter.sets];
    const filterOperator = mustMatchAll ? 'AND' : 'OR';

    if(sets){
      const filterKeys = Object.keys(sets);
      let filter = [];
      if(filterKeys && filterKeys.length){
        filter = filterKeys
        .map(key => {
          const set = buildSet(sets[key], isForSaving);
          if (!set) return null;

          if (filterKeys.length <= 1) {
            return set;
          }

          return `${set}`;
        })?.filter(set => set);
      }
      
      let datasetValue = options.Dataset
      if(userInfo)
      {
        const isUserPostfire = userInfo[FieldNames.userFields.isPostFire] === FieldNames.yesNo.yes;
        const isUserFuels = userInfo[FieldNames.userFields.isFuels] === FieldNames.yesNo.yes;
      
        if(isUserFuels && !isUserPostfire)
        {
          datasetValue = FieldNames.datasets.fuels;
        }
        else if(!isUserFuels && isUserPostfire)
        {
          datasetValue = FieldNames.datasets.postfire;
        }
      }
      const datasetWhere = isForSaving ? "" : getDatasetFilter(datasetValue);
      if (filter?.length > 1) {
        return "(" + filter.join(` ${filterOperator} `) + ")"  + (datasetWhere ? ` AND ${datasetWhere}` : "");
      }
      else if(filter?.length === 1)
      {
        return filter?.[0] + (datasetWhere ? ` AND ${datasetWhere}` : "");
      }
      else
      {
        return '';
      }
    }
  }

  return '';
}
